<template>
  <div class="col full-width overflow-auto">
    <div
      :style="{
        position: 'sticky',
        top: '0',
        backgroundColor: 'white',
        zIndex: '2',
      }"
    >
      <div class="q-px-2x-md q-py-xl row items-start">
        <q-avatar
          color="info-20"
          font-size="43px"
          size="86px"
          text-color="info-70"
        >
          <span class="text-uppercase">
            {{ userAvatarInitials }}
          </span>
          <q-badge color="transparent" floating class="">
            <Menu v-if="computedApplicationStatus.label" placement="right">
              <component
                :is="computedApplicationStatus.icon"
                class="icon"
                :class="computedApplicationStatus.class"
              />
              <template #popper>
                <div>
                  <div>{{ computedApplicationStatus.label }}</div>

                  <div
                    v-if="shouldShowApplicationStatusReason"
                    class="q-mt-xxs flex no-wrap"
                    :style="{ maxWidth: '300px' }"
                  >
                    <span class="q-mr-xxs">Reason:</span>
                    <span class="text-xs text-wrap">
                      {{ computedApplicationStatus.reason }}
                    </span>
                  </div>
                </div>
              </template>
            </Menu>
          </q-badge>
          <PTooltip>{{ user?.name || "" }}</PTooltip>
        </q-avatar>
        <div class="q-ml-md col-7">
          <div class="text-sm text-weight-medium">
            {{ isTeamMembers ? user?.name : user.shop_name }}
          </div>
          <div
            v-if="['buyer', 'seller'].includes(user.role)"
            class="text-xxs text-neutral-80 q-mt-xxs"
          >
            <span
              >GSTIN:
              {{
                user?.gstin ||
                (user?.businessDetails?.is_gst_registered == null
                  ? "-"
                  : "Unregistered")
              }}</span
            >

            <span class="q-ml-lg"
              >Org Code:
              <PBtn
                flat
                dense
                fluid
                class="q-pa-none"
                target="_blank"
                :to="`/users/organisations?number=${user?.organisation_name}`"
              >
                <span class="text-xxs">
                  {{ user?.organisations?.internal_number || "-" }}
                </span>
              </PBtn>
            </span>
          </div>
          <div class="flex items-center text-xxs text-neutral-80">
            <div class="flex items-center">
              <EmailIcon class="icon--xxxs q-mr-xxs" />
              <span>{{ user.email || "NA" }}</span>
            </div>
            <div class="flex items-center q-ml-md">
              <CallIcon class="icon--xxxs q-mr-xxs" />
              <span>{{ user.phone || "-" }}</span>
            </div>
            <div v-if="showChatIcon" class="flex items-center q-ml-md">
              <PBtn
                flat
                dense
                :loading="creatingConversation"
                class="text-primary text-xxs q-pa-none"
                @click="handleOpenChat"
              >
                <ChatIcon class="icon--xxxs q-mr-xxs" />
                Chat
              </PBtn>
            </div>
          </div>
          <div
            v-if="['buyer', 'seller'].includes(user.role)"
            class="flex no-wrap text-xxs text-neutral-80 q-mt-xs"
          >
            <AddressIcon class="icon--xxxs q-mr-xxs" />
            <span>{{
              user.pincode ? user.address[0] + ", " + user.pincode : "-"
            }}</span>
          </div>
        </div>
        <div
          v-if="['in_review'].includes(user.application_status)"
          class="column justify-between items-end text-sm text-weight-medium q-ml-none"
          :style="{ marginLeft: 'auto', height: '86px' }"
        >
          <PBtn
            flat
            dense
            color="success-70"
            :disable="!canApprove"
            :loading="applicationStatusLoading.approved"
            @click="handleApplicationStatusChange('approved')"
          >
            <ApproveUserIcon class="icon--sm q-mr-xs" />
            <span>Approve</span>
          </PBtn>
          <PBtn
            flat
            dense
            color="error-70"
            :disable="!canReject"
            :loading="applicationStatusLoading.rejected"
            @click="handleApplicationStatusChange('rejected')"
          >
            <RejectUserIcon class="icon--sm q-mr-xs" />
            <span>Reject</span>
          </PBtn>
        </div>
        <div
          v-else-if="['approved', 'rejected'].includes(user.application_status)"
          :style="{ marginLeft: 'auto' }"
        >
          <PBtn flat dense :loading="menuLoading || isUpdatingUserStatus">
            <MenuIcon class="icon" />
            <q-menu
              :offset="[30, 8]"
              anchor="bottom left"
              self="top middle"
              fit
              :style="{ width: '180px', borderRadius: '4px' }"
            >
              <q-list>
                <q-item
                  v-for="(
                    { label, onClick, disable, icon, classes }, index
                  ) in profileMenuItems"
                  :key="index"
                  v-close-popup
                  clickable
                  dense
                  :disable="disable"
                  @click="onClick"
                >
                  <q-item-section no-wrap>
                    <q-item-label
                      :title="label"
                      class="text-primary-60 text-xs text-weight-medium flex items-center"
                      lines="1"
                    >
                      <component
                        :is="icon"
                        :class="`icon--xs q-mr-xs ${classes}`"
                      ></component>
                      <span :class="`${classes}`"> {{ label }} </span>
                    </q-item-label>
                  </q-item-section>
                </q-item>
              </q-list>
            </q-menu>
          </PBtn>
        </div>
      </div>

      <q-separator v-if="!isInternalUser || isAgent" color="neutral-40" />

      <div v-if="!isInternalUser || isAgent" class="q-px-2x-md q-py-md">
        <PTabs
          v-model="activeTab"
          :hide-arrows="isAgent"
          content-class="overflow-auto justify-start"
          indicator="chip"
          outside-arrows
        >
          <PTab
            v-for="({ name, label, icon }, index) in tabs"
            :key="index"
            :class="{ 'text-neutral-70': activeTab !== name }"
            :disable="isTabDisabled(name)"
            :name="name"
            :style="{ borderRadius: '12px' }"
            class="text-xs text-weight-medium"
            indicator="chip"
            tab-class="chip"
          >
            <template #icon>
              <component :is="icon" class="icon--sm q-mr-xs"></component>
            </template>
            {{ label }}
          </PTab>
        </PTabs>
      </div>

      <q-separator color="neutral-40" />
    </div>
    <q-tab-panels v-model="activeTab" animated class="col overflow-auto">
      <q-tab-panel name="Business & KYC" class="q-pa-none full-height">
        <BusinessKycForm />
      </q-tab-panel>
      <q-tab-panel name="Financial" class="q-pa-none">
        <BankDetails v-if="user?.basicInfo?.role !== 'buyer'" />
        <TaxSettings />
        <PaymentTerms />
      </q-tab-panel>
      <q-tab-panel name="Addresses" class="q-pa-none">
        <AddressForm />
      </q-tab-panel>
      <q-tab-panel name="Documents" class="full-height">
        <DocumentTable />
      </q-tab-panel>
      <q-tab-panel
        name="Credit Evaluation Summary"
        class="q-pa-none full-height"
      >
        <CreditEvaluationForm />
      </q-tab-panel>
      <q-tab-panel name="Others" class="q-pa-none">
        <BasicInfoForm
          v-model:is-duplicate-value="isDuplicateValue"
          :team-members="isTeamMembers"
        />
        <POSettingForm v-if="unref(user)?.basicInfo?.role === 'buyer'" />
        <OthersForm v-if="!unref(isInternalUser)" />
      </q-tab-panel>
      <q-tab-panel name="Associated Users" class="q-pa-none full-height">
        <AssociatedUsers :is-agent="isAgent" />
      </q-tab-panel>
      <q-tab-panel name="Activities" class="q-pa-none full-height">
        <UserActivites />
      </q-tab-panel>
      <q-tab-panel name="Communications" class="q-pa-none full-height">
        <CommunicationSettings :user-id="user?._id" :role="user?.role" />
      </q-tab-panel>
    </q-tab-panels>
  </div>
</template>
<script setup>
import EmailIcon from "assets/icons/email.svg";
import CallIcon from "assets/icons/call.svg";
import ChatIcon from "assets/icons/conversation.svg";
import ApproveUserIcon from "assets/icons/approve_user.svg";
import RejectUserIcon from "assets/icons/reject_user.svg";
import MenuIcon from "assets/icons/menu.svg";
import ReviewIcon from "assets/icons/review.svg";
import RoundCheckIcon from "assets/icons/check-round-filled.svg";
import ExclamationIcon from "assets/icons/exclamation-round-filled.svg";
import DeniedIcon from "assets/icons/denied-circle.svg";
import EditIcon from "assets/icons/edit.svg";
import ActivateUserIcon from "assets/icons/activate_user.svg";
import DeactivateUserIcon from "assets/icons/deactivate_user.svg";
import AddressIcon from "assets/icons/address.svg";
import BusinessDetailIcon from "assets/icons/business.svg";
import FinancialIcon from "assets/icons/financial.svg";
import DocumentsIcon from "assets/icons/document.svg";
import CreditIcon from "assets/icons/credit.svg";
import OtherDetailIcon from "assets/icons/other-detail.svg";
import UserGroupIcon from "assets/icons/user-group.svg";
import MarkOperationalIcon from "assets/icons/non-operational-document-icon.svg";
import MarkNonOperationalIcon from "assets/icons/operational-document-icon.svg";
import MinusIcon from "assets/icons/minus-circle.svg";
import CircleHistoryIcon from "assets/icons/circle-history.svg";
import CommunicationSettingsIcon from "assets/icons/communication-setting.svg";

import { Menu } from "floating-vue";
import "floating-vue/dist/style.css";

import PDialogForm from "src/components/PDialogForm.vue";

import { useQuasar } from "quasar";

import { useChangeCase } from "@vueuse/integrations/useChangeCase";

const {
  putUserStatus,
  putApplicationStatusDetails,
  putOperationalUserStatus,
  getUser,
  setAssociatedUser,
  toggleUserForm,
} = useUserStore();

const { reset } = useCommunicationSettingsStore();

const { getlastUserActiveStatus, deactivateOrg } = useOrganisationStore();

const { canActivate, canDeactivate, canApprove, canReject } =
  useUserPermissions();

const $q = useQuasar();

const { formState, user, isAssociatedUser } = storeToRefs(useUserStore());

const hashRole = inject("hashRole");

const onGoingOnboarding = ref(false);
const isDuplicateValue = ref(false);
let previousUserId = null;
const loadingState = ref(false);

const isInternalUser = computed(() => {
  return unref(user).is_internal_user;
});

const showChatIcon = computed(() => {
  return unref(user)?.phone && ["buyer", "seller"].includes(unref(user)?.role);
});

const isAgent = computed(() => {
  return unref(user).role === "agent";
});

const activeTab = ref("Business & KYC");

const changeActiveTab = (tab) => {
  activeTab.value = tab;
};

provide("activeTab", activeTab);

provide("onGoingOnboarding", readonly(onGoingOnboarding));

provide("updateActiveTab", changeActiveTab);

const isTeamMembers = inject("isTeamMembers");

watchEffect(() => {
  if (unref(formState).open && unref(isInternalUser)) {
    activeTab.value = "Others";
  }
});

const computedAssociatedLabel = computed(() => {
  let label = "ASSOCIATED ";

  if (unref(user)?.basicInfo?.role === "seller") {
    label += "CUSTOMERS";
  } else if (unref(user)?.basicInfo?.role === "buyer") {
    label += "VENDORS";
  } else {
    label += "CUSTOMERS & VENDORS";
  }

  return label;
});

const tabs = computed(() => {
  const role = unref(user)?.basicInfo?.role;

  const allTabs = [
    {
      name: "Business & KYC",
      label: `BUSINESS & KYC`,
      icon: BusinessDetailIcon,
    },
    {
      name: "Financial",
      label: `FINANCIAL`,
      icon: FinancialIcon,
    },
    {
      name: "Addresses",
      label: `ADDRESSES`,
      icon: AddressIcon,
    },
    {
      name: "Documents",
      label: `DOCUMENTS`,
      icon: DocumentsIcon,
    },
    {
      name: "Credit Evaluation Summary",
      label: `CREDIT EVALUATION SUMMARY`,
      icon: CreditIcon,
    },
    {
      name: `Associated Users`,
      label: computedAssociatedLabel.value,
      icon: UserGroupIcon,
    },
    {
      name: "Others",
      label: `OTHERS`,
      icon: OtherDetailIcon,
    },
    {
      name: "Activities",
      label: `ACTIVITES`,
      icon: CircleHistoryIcon,
    },
    {
      name: "Communications",
      label: `COMMUNICATIONS`,
      icon: CommunicationSettingsIcon,
    },
  ];

  if (isAgent.value) {
    const agentTabs = allTabs.filter((tab) =>
      ["Others", "Associated Users"].includes(tab.name),
    );

    agentTabs.sort((a, b) => {
      if (a.name === "Others") return -1;

      if (b.name === "Others") return 1;

      return 0;
    });

    return agentTabs;
  }

  return allTabs.filter((tab) => {
    if (tab.name === "Associated Users") {
      return ["seller", "buyer"].includes(role);
    }

    return true;
  });
});

const isTabDisabled = computed(() => {
  const { role, application_stage, application_status } = unref(user) || {};

  return (name) => {
    if (!role || !application_stage) return true;

    // KAM: only "Others" enabled
    if (role === "kam") {
      return name !== "Others";
    }

    // Agent: only "Others" and "Associated Users" enabled
    if (role === "agent") {
      return !["Others", "Associated Users"].includes(name);
    }

    // Addresses / Documents / Credit Eval need business completion
    if (
      ["Addresses", "Documents", "Credit Evaluation Summary"].includes(name)
    ) {
      return !application_stage?.business?.is_completed;
    }

    // Associated Users only for approved seller/buyer
    if (name === "Associated Users") {
      return (
        ["seller", "buyer"].includes(role) && application_status !== "approved"
      );
    }

    // Default: enable only from allowed list
    return ![
      "Business & KYC",
      "Others",
      "Documents",
      "Financial",
      "Addresses",
      "Activities",
      "Communications",
    ].includes(name);
  };
});

const { user: loggedInUser } = storeToRefs(useAuthStore());

const editProfile = () => {
  activeTab.value = "Others";
};

const confirmToggleStatus = async () => {
  const isUserActive = unref(user).is_active;
  const action = isUserActive ? "Deactivate" : "Activate";
  const role = unref(user)?.role;
  let userType;

  if (role === "seller") {
    userType = "Vendor";
  } else if (role === "buyer") {
    userType = "Customer";
  } else {
    userType = "User";
  }

  try {
    const response = await getlastUserActiveStatus(
      unref(user)?.organisations?._id,
      unref(user)?._id,
    );
    const orgName = response?.orgName;
    const orgIsActive = response.isActive;
    let dialogText;

    if (orgIsActive) {
      dialogText = `Are you sure? This will also deactivate its organisation ${orgName}`;
    } else {
      dialogText = `Are you sure you want to ${isUserActive ? "deactivate" : "activate"} this ${userType}?`;
    }

    const confirmationDialog = $q.dialog({
      component: PDialogForm,
      componentProps: {
        title: `${action} ${userType}`,
        label: dialogText,
        textClass: "text-md text-neutral text-weight-regular",
        labelClass: "bg-none",
        cardStyle: { minWidth: "600px" },
        submitLabel: orgIsActive ? "Proceed" : "Yes",
        cancelLabel: orgIsActive ? "Cancel" : "No",
        cancellable: true,
        closable: true,
        form: {
          input: {
            type: "textarea",
            autogrow: true,
            placeholder: "Enter remarks",
            maxlength: "100",
            label: "Remarks",
            labelClasses: "text-sm text-neutral",
            validations: { label: "Remarks", required: true },
          },
        },
        onConfirm: (reason) => {
          if (orgIsActive) {
            updateOrgAndUserStatus(reason, confirmationDialog);
          } else {
            updateUserStatus(reason, confirmationDialog);
          }
        },
      },
    });
  } catch (error) {
    console.error("Failed to fetch user status:", error);
  }
};

const updateOrgAndUserStatus = async (reason, confirmationDialog) => {
  try {
    isUpdatingUserStatus.value = true;

    confirmationDialog?.hide();

    const payload = {
      ...unref(user),
      reason: unref(reason),
    };

    await Promise.all([
      putUserStatus(payload),
      deactivateOrg(unref(user)?.organisations?._id),
    ]);

    isUpdatingUserStatus.value = false;

    editClose();
  } catch (error) {
    isUpdatingUserStatus.value = false;

    console.error("Failed to update organization and user status:", error);
  }
};

const menuLoading = ref(false);

const confirmNonOperationalStatus = async () => {
  const isOperational = unref(user)?.is_operational ?? true;

  if (isOperational) {
    const confirmationDialog = $q.dialog({
      component: PDialogForm,
      componentProps: {
        title: `Mark this ${unref(user)?.shop_name} as non-operational`,
        labelClass: "bg-none",
        cardStyle: { minWidth: "600px" },
        submitLabel: "Submit",
        form: {
          input: {
            type: "textarea",
            autogrow: true,
            placeholder: "Enter reason",
            label: "Reason for marking this business as non-operational",
            labelClasses: "text-sm text-neutral",
            validations: { label: "Rejection reason", required: true },
          },
        },
        cancellable: false,
        closable: true,
        onConfirm: (reason) =>
          updateOperationalStatus(reason, confirmationDialog),
      },
    });
  } else {
    menuLoading.value = true;

    await putOperationalUserStatus({ is_operational: true });

    menuLoading.value = false;

    editClose();
  }
};

const updateOperationalStatus = async (reason, confirmationDialog) => {
  try {
    menuLoading.value = true;

    confirmationDialog?.hide();

    await putOperationalUserStatus({
      is_operational: false,
      reason: unref(reason),
    });

    editClose();
  } catch (error) {
    console.log(error);
  } finally {
    menuLoading.value = false;
  }
};

import { getAvatarInitials } from "src/assets/scripts/utilities/helpers";

const userAvatarInitials = computed(() => {
  return getAvatarInitials(unref(user)?.name);
});

const isUpdatingUserStatus = ref(false);

const updateUserStatus = async (reason, confirmationDialog) => {
  try {
    isUpdatingUserStatus.value = true;

    confirmationDialog?.hide();

    const promises = [];

    const payload = {
      ...unref(user),
      reason: unref(reason),
    };

    promises.push(putUserStatus(payload));

    await Promise.all(promises);

    isUpdatingUserStatus.value = false;

    editClose();
  } catch {
    isUpdatingUserStatus.value = false;
  }
};

const deactivateUser = () => {
  confirmToggleStatus();
};

const userStatusMenuItem = computed(() => {
  if (!unref(formState).edit) return {};

  const { is_active, _id: userId, application_status } = unref(user);
  const { _id: loggedInUserId } = unref(loggedInUser);
  const isCurrentUser = loggedInUserId === userId;

  return {
    label: is_active ? "Deactivate" : "Activate",
    disable:
      isCurrentUser ||
      application_status === "rejected" ||
      (is_active ? !canDeactivate.value : !canActivate.value),
    icon: is_active ? DeactivateUserIcon : ActivateUserIcon,
    onClick: deactivateUser,
    classes: is_active ? "text-error-70" : "text-success-70",
  };
});

const operationalStatusMenuItem = computed(() => {
  if (!unref(formState).edit) return {};

  const {
    is_active,
    is_operational = true,
    _id: userId,
    application_status,
  } = unref(user);
  const { _id: loggedInUserId } = unref(loggedInUser);
  const isCurrentUser = loggedInUserId === userId;

  return {
    label: is_operational ? "Mark non-operational" : "Mark Operational",
    disable: isCurrentUser || application_status === "rejected" || !is_active,
    icon: is_operational ? MarkOperationalIcon : MarkNonOperationalIcon,
    onClick: confirmNonOperationalStatus,
    classes: is_active ? "text-primary" : "text-primary-60",
  };
});

const computedApplicationStatus = computed(() => {
  const {
    application_status = "",
    is_active,
    is_operational = true,
    non_ops_reason = "",
    remarks = [],
  } = unref(user);
  const activeRemark = remarks.find((remark) => remark.type === "active");
  const inactiveRemark = remarks.find((remark) => remark.type === "inactive");

  if (application_status === "approved") {
    if (!is_active) {
      return {
        label: "Deactivated",
        icon: DeniedIcon,
        class: "text-neutral-70",
        reason: inactiveRemark?.remark || "",
      };
    }

    return {
      label: "Approved",
      icon: RoundCheckIcon,
      class: "text-success-70",
      reason: activeRemark?.remark || "",
    };
  }

  if (!is_operational)
    return {
      label: `Non-Operational.${non_ops_reason && " Reason: " + non_ops_reason}`,
      icon: MinusIcon,
      class: "text-warning-60",
    };

  if (application_status === "rejected") {
    const rejectedRemark = remarks.find((remark) => remark.type === "rejected");

    return {
      label: `Rejected. ${rejectedRemark?.remark || ""}`,
      icon: ExclamationIcon,
      class: "text-error-60",
    };
  }

  return {
    label: useChangeCase(application_status, "capitalCase").value,
    ...statusIconClassMap[application_status],
  };
});

const statusIconClassMap = {
  in_review: {
    icon: ReviewIcon,
    class: "text-neutral-60",
  },
  incomplete: {
    icon: ReviewIcon,
    class: "text-neutral-60",
  },
  approved: {
    icon: RoundCheckIcon,
    class: "text-success-70",
  },
};

const shouldShowApplicationStatusReason = computed(() => {
  const { application_status = "" } = unref(user);
  const { reason } = unref(computedApplicationStatus);

  return application_status === "approved" && Boolean(reason);
});

const profileMenuItems = computed(() => {
  const isInternalUser = unref(user)?.is_internal_user;

  return [
    {
      label: "Edit",
      onClick: editProfile,
      disable: !unref(user)?.is_active,
      icon: EditIcon,
      classes: unref(user)?.is_active ? "text-primary" : "text-primary-60",
    },
    isInternalUser ? null : unref(operationalStatusMenuItem),
    unref(userStatusMenuItem),
  ].filter(Boolean);
});

const applicationStatusLoading = ref({
  approved: false,
  rejected: false,
});

const handleApplicationStatusChange = (applicationStatus) => {
  const confirmationDialog = $q.dialog({
    component: PDialogForm,
    componentProps: {
      title: `${applicationStatus === "approved" ? "Approve" : "Reject "} ${unref(user).shop_name}`,
      submitLabel: `${applicationStatus === "approved" ? "Approve" : "Reject "}`,
      form: {
        input: {
          placeholder: `${applicationStatus === "approved" ? "Enter remarks" : "Enter reason "}`,
          label: `${applicationStatus === "approved" ? "Remarks" : "Rejection reason "}`,
          labelClasses: "text-sm text-neutral",
          type: "textarea",
          help: `${applicationStatus === "approved" ? "Please add any addition remark" : "Please state the reason to reject this user"}`,
          helpClasses: "text-xs text-neutral-70 q-mt-none",
          validations: {
            label: `${applicationStatus === "approved" ? "Remarks" : "Rejection reason "}`,
            required: true,
          },
        },
      },
      note: "Note: This action is irreversible. Please take caution.",
      noteClass: "text-negative",
      cancellable: false,
      loading: loadingState,
      onConfirm: async (approveRejectReason) => {
        try {
          applicationStatusLoading.value[applicationStatus] = true;

          loadingState.value = true;

          confirmationDialog.hide();

          await putApplicationStatusDetails({
            status: applicationStatus,
            reason: unref(approveRejectReason),
            organisation: { _id: unref(user)?.organisations?._id },
            role: hashRole.value,
          });

          editClose();

          loadingState.value = false;
        } catch (err) {
          console.error(err);

          loadingState.value = false;
        } finally {
          applicationStatusLoading.value[applicationStatus] = false;
        }
      },
    },
  });
};

watch(
  () => unref(user)?._id,
  (newUserId, oldUserId) => {
    if (newUserId && newUserId !== oldUserId && newUserId !== previousUserId) {
      activeTab.value = "Business & KYC";

      previousUserId = newUserId;
    }
  },
);

const editCloseLoading = ref(false);

const editClose = async () => {
  onGoingOnboarding.value = false;

  if (unref(isAssociatedUser)._id.length) {
    try {
      editCloseLoading.value = true;

      await getUser(unref(isAssociatedUser)._id);

      editCloseLoading.value = false;
    } catch {
      editCloseLoading.value = false;
    }

    setAssociatedUser();

    activeTab.value = "Associated Users";
  } else {
    toggleUserForm({
      open: false,
      edit: false,
    });

    activeTab.value = "Business & KYC";
  }
};

const { openConversationInbox, normalizePhone } = useChatUtils();
const {
  createConversation,
  creatingConversation,
  getOrCreateConversationContact,
} = useChatContactUtils();

// Handle opening chat with user's phone number
const handleOpenChat = async () => {
  const userData = unref(user);

  const { phone, role, shop_name, name, gstin, pan, conversationSid } =
    userData || {};

  if (!phone) {
    console.warn("No phone number available for this user");

    return;
  }

  const firstName = name?.split(" ")?.[0] || "";
  const lastName = name?.split(" ")?.[1] || "";
  const normalizedPhone = normalizePhone(phone);

  try {
    const contact = await getOrCreateConversationContact({
      attributes: {
        firstName,
        lastName,
        gstNumber: gstin || "",
        panNumber: pan || "",
        role,
      },
      businessName: shop_name || name || "",
      conversationSid,
      firstName,
      lastName,
      phone: normalizedPhone,
      role,
    });

    const conversation = await createConversation(contact);

    openConversationInbox(conversation);
  } catch (error) {
    console.error(error);
  }
};

watch(
  () => unref(activeTab),
  (tab) => {
    if (tab !== "Communications") {
      reset();
    }
  },
);

defineExpose({ editClose, editCloseLoading });

onBeforeUnmount(reset);
</script>
